import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { Button } from "components/DesignSystem/Button/Button";
import Loader from "components/DesignSystem/Loader/loader";
import { AmountSuperScript } from "components/design/AmountSuperScript";
import { useModal } from "hooks/useModal";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useToast } from "hooks/useToast";
import RadioGroup from "components/DesignSystem/RadioGroup/RadioGroup";
import { useGetIncomeStatementUploadQuery } from "store/apis/estimatedTax";
import { UploadedStatementDetailModal } from "components/AdvanceTaxAndProfits/UploadedStatementDetailModal";
import { UploadIncomeStatementModal } from "components/AdvanceTaxAndProfits/UploadIncomeStatementModal";
import { IncomeStatementUploadResult } from "types/Models/estimatedTax";
import { formatDate } from "utils/formatDate";
import { MM_SPACE_YYYY } from "constants/date";
import {
  FinancialClosingsTable,
  BusinessFinancialClosing,
} from "./FinancialClosingsTable";
import { InkleManagedBanner } from "./InkleManagedBanner";
import { InlineIncomeStatementUpload } from "./InlineIncomeStatementUpload";
import { MonthlyBookkeepingUpsell } from "./MonthlyBookkeepingUpsell";

type BusinessFinancialsSource =
  | "MONTHLY_BOOKKEEPING"
  | "ANNUAL_CATCHUP"
  | "UPLOAD_REQUIRED";

const EMPTY_OTHER_YEARS_WITH_UPLOADS: never[] = [];

type BusinessFinancialsExistingUpload = {
  uuid: string;
  tax_year: number;
  period_start_date: string;
  period_end_date: string;
  net_income: number;
};

type FinancialsWindow = {
  window_start: string;
  window_end: string;
};

type OtherYearUpload = {
  tax_year: number;
  uploaded_at: string;
  net_income: number;
};

export type BusinessFinancialsData = {
  source: BusinessFinancialsSource;
  has_financials: boolean;
  financial_closings: BusinessFinancialClosing[];
  existing_upload: BusinessFinancialsExistingUpload | null;
  financials_window?: FinancialsWindow;
  other_years_with_uploads?: OtherYearUpload[];
};

type Props = {
  data: BusinessFinancialsData;
  entityId: string;
  year: string;
  quarter?: number;
};

const formatWindowMonth = (date: string) => {
  const parsed = dayjs(date);
  return parsed.isValid() ? parsed.format(MM_SPACE_YYYY) : null;
};

const getFinancialsWindowLabel = (
  quarter: number | undefined,
  year: string,
  financialsWindow: FinancialsWindow | undefined
) => {
  const fallback = "Do you have your income statement for this quarter?";
  if (!quarter || !financialsWindow) {
    return fallback;
  }
  const start = formatWindowMonth(financialsWindow.window_start);
  const end = formatWindowMonth(financialsWindow.window_end);
  if (!start || !end) {
    return fallback;
  }
  return `Do you have your income statement from ${start} through at least ${end} for Quarter ${quarter} of tax year ${year}?`;
};

const HAS_FINANCIALS_YES = "YES";
const HAS_FINANCIALS_NO = "NO";
type HasFinancialsAnswer = typeof HAS_FINANCIALS_YES | typeof HAS_FINANCIALS_NO;

const isUploadDetail = (
  data: IncomeStatementUploadResult | Record<string, never> | undefined
): data is IncomeStatementUploadResult => Boolean(data && "uuid" in data);

const UploadSummaryCard = ({
  entityId,
  taxYear,
  netIncome,
  fallbackLabel,
  onReupload,
}: {
  entityId: string;
  taxYear: number;
  netIncome: number;
  fallbackLabel: string;
  onReupload: () => void;
}) => {
  const detailModal = useModal();
  const { alertToast } = useToast();
  const {
    data: uploadDetail,
    isFetching: isUploadDetailFetching,
    isError: isUploadDetailError,
  } = useGetIncomeStatementUploadQuery({
    entityId,
    taxYear: String(taxYear),
  });
  const uploadDetailData = isUploadDetail(uploadDetail) ? uploadDetail : null;
  const hasUploadDetail = uploadDetailData !== null;

  const handleViewOrReplaceClick = () => {
    if (isUploadDetailError || (!isUploadDetailFetching && !hasUploadDetail)) {
      alertToast({
        message: "Couldn't load the uploaded statement. Please try again.",
      });
      return;
    }
    detailModal.open();
  };

  const handleReuploadClick = () => {
    detailModal.close();
    onReupload();
  };

  const periodLabel = uploadDetailData ? (
    <>
      {formatDate(uploadDetailData.period_start_date)} –{" "}
      {formatDate(uploadDetailData.period_end_date)}
    </>
  ) : (
    fallbackLabel
  );

  return (
    <div className="t-flex t-items-center t-justify-between t-p-4 t-border t-border-neutral-10 t-rounded-lg t-bg-surface-lighter-grey">
      <div className="t-flex t-flex-col t-gap-1">
        <span className="t-text-subtext-sm t-font-medium t-text-text-30">
          {periodLabel}
        </span>
        <span className="t-text-body-sm t-text-text-100">
          Net Income: <AmountSuperScript amount={netIncome} />
        </span>
      </div>
      <Button
        type="button"
        customType="secondary"
        size="small"
        isLoading={isUploadDetailFetching}
        onClick={handleViewOrReplaceClick}
      >
        View
      </Button>
      {detailModal.isOpen && uploadDetailData && (
        <UploadedStatementDetailModal
          open={detailModal.isOpen}
          onClose={detailModal.close}
          upload={uploadDetailData}
          onReupload={handleReuploadClick}
        />
      )}
    </div>
  );
};

const ExistingUploadSummary = ({
  existingUpload,
  entityId,
  year,
  quarter,
}: {
  existingUpload: BusinessFinancialsExistingUpload;
  entityId: string;
  year: string;
  quarter?: number;
}) => {
  const [isReuploading, setIsReuploading] = useState(false);

  if (isReuploading) {
    return (
      <InlineIncomeStatementUpload
        entityId={entityId}
        year={year}
        quarter={quarter}
      />
    );
  }

  return (
    <UploadSummaryCard
      entityId={entityId}
      taxYear={existingUpload.tax_year}
      netIncome={existingUpload.net_income}
      fallbackLabel={`${formatDate(
        existingUpload.period_start_date
      )} – ${formatDate(existingUpload.period_end_date)}`}
      onReupload={() => setIsReuploading(true)}
    />
  );
};

const PreviousYearUploadRow = ({
  entityId,
  currentTaskYear,
  otherYearUpload,
}: {
  entityId: string;
  currentTaskYear: string;
  otherYearUpload: OtherYearUpload;
}) => {
  const reuploadModal = useModal();
  const [isSyncing, setIsSyncing] = useState(false);

  useEffect(() => {
    if (isSyncing) setIsSyncing(false);
  }, [otherYearUpload, isSyncing]);

  if (isSyncing) {
    return (
      <div className="t-flex t-flex-col t-items-center t-gap-2 t-p-4">
        <Loader customType="secondary" />
        <span className="t-text-body-sm t-text-text-30">
          Syncing income statement to estimated tax…
        </span>
      </div>
    );
  }

  return (
    <div className="t-relative">
      <UploadSummaryCard
        entityId={entityId}
        taxYear={otherYearUpload.tax_year}
        netIncome={otherYearUpload.net_income}
        fallbackLabel={`Tax year ${otherYearUpload.tax_year}`}
        onReupload={reuploadModal.open}
      />
      {reuploadModal.isOpen && (
        <UploadIncomeStatementModal
          open={reuploadModal.isOpen}
          onClose={reuploadModal.close}
          entityId={entityId}
          year={currentTaskYear}
          showYearSelector
          initialSelectedYear={String(otherYearUpload.tax_year)}
          onUploadSuccess={() => setIsSyncing(true)}
        />
      )}
    </div>
  );
};

const UploadRequiredView = ({
  entityId,
  year,
  quarter,
  initialHasFinancials,
  financialsWindow,
  otherYearsWithUploads,
}: {
  entityId: string;
  year: string;
  quarter?: number;
  initialHasFinancials: boolean;
  financialsWindow?: FinancialsWindow;
  otherYearsWithUploads: OtherYearUpload[];
}) => {
  const [hasFinancials, setHasFinancials] = useState<HasFinancialsAnswer>(
    initialHasFinancials ? HAS_FINANCIALS_YES : HAS_FINANCIALS_NO
  );
  const [isSyncingPreviousYear, setIsSyncingPreviousYear] = useState(false);

  const previousYearUploadModal = useModal();

  useEffect(() => {
    if (isSyncingPreviousYear) setIsSyncingPreviousYear(false);
  }, [otherYearsWithUploads, isSyncingPreviousYear]);

  if (isSyncingPreviousYear) {
    return (
      <div className="t-flex t-flex-col t-items-center t-gap-2 t-py-6">
        <Loader customType="secondary" />
        <span className="t-text-body-sm t-text-text-30">
          Syncing income statement to estimated tax…
        </span>
      </div>
    );
  }

  if (otherYearsWithUploads.length > 0) {
    return (
      <div className="t-flex t-flex-col t-gap-2">
        <div className="t-flex t-flex-col t-gap-2">
          <span className="t-text-body-sm t-font-medium t-text-text-60">
            Uploaded financial for year {otherYearsWithUploads[0].tax_year}
          </span>
          <div className="t-border t-border-solid t-border-neutral-10 t-rounded-lg">
            {otherYearsWithUploads.map((otherYearUpload) => (
              <PreviousYearUploadRow
                key={otherYearUpload.tax_year}
                entityId={entityId}
                currentTaskYear={year}
                otherYearUpload={otherYearUpload}
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="t-flex t-flex-col t-gap-4">
      <RadioGroup.Root
        value={hasFinancials}
        onValueChange={(value) =>
          setHasFinancials(value as HasFinancialsAnswer)
        }
      >
        <RadioGroup.Content>
          <p className="t-m-0 t-text-body-sm t-text-text-30">
            {getFinancialsWindowLabel(quarter, year, financialsWindow)}
          </p>
          <RadioGroup.Item value={HAS_FINANCIALS_YES}>
            Yes, I have
          </RadioGroup.Item>
          <RadioGroup.Item value={HAS_FINANCIALS_NO}>
            No, I don't have
          </RadioGroup.Item>
        </RadioGroup.Content>
      </RadioGroup.Root>

      {hasFinancials === HAS_FINANCIALS_YES && (
        <InlineIncomeStatementUpload
          entityId={entityId}
          year={year}
          quarter={quarter}
        />
      )}

      {hasFinancials === HAS_FINANCIALS_NO && (
        <>
          <p className="t-m-0 t-text-body-sm t-text-text-30">
            You can request your bookkeeper to provide an income statement or
            have it prepared through Inkle. Our team of dedicated bookkeepers
            will handle the preparation of your income statement for you.
          </p>
          <MonthlyBookkeepingUpsell entityId={entityId} season={year} />

          <p className="t-m-0 -t-mt-2 t-text-body-sm t-text-text-30 t-italic">
            Have financials from a previous year?{" "}
            <Button
              type="button"
              customType="link"
              onClick={previousYearUploadModal.open}
              className="t-italic t-no-underline"
            >
              Click here
            </Button>{" "}
            to upload them.
          </p>
          <UploadIncomeStatementModal
            open={previousYearUploadModal.isOpen}
            onClose={previousYearUploadModal.close}
            entityId={entityId}
            year={year}
            showYearSelector
            onUploadSuccess={() => setIsSyncingPreviousYear(true)}
          />
        </>
      )}
    </div>
  );
};

export const BusinessFinancialsField = ({
  data,
  entityId,
  year,
  quarter,
}: Props) => {
  const { isAdmin } = useRoleBasedView();

  if (data.source === "UPLOAD_REQUIRED") {
    if (data.existing_upload) {
      return (
        <ExistingUploadSummary
          existingUpload={data.existing_upload}
          entityId={entityId}
          year={year}
          quarter={quarter}
        />
      );
    }
    return (
      <UploadRequiredView
        entityId={entityId}
        year={year}
        quarter={quarter}
        initialHasFinancials={data.has_financials}
        financialsWindow={data.financials_window}
        otherYearsWithUploads={
          data.other_years_with_uploads ?? EMPTY_OTHER_YEARS_WITH_UPLOADS
        }
      />
    );
  }

  if (isAdmin) {
    return (
      <FinancialClosingsTable financialClosings={data.financial_closings} />
    );
  }

  return <InkleManagedBanner />;
};
